import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCreateEventsUIStore } from './createEvent.ui-store'
import { DEFAULT_EVENT_DURATION_MINUTES } from '../constants'
import { roundToNext30Minutes } from '../helpers'
import type { EventSearchResult } from '../types/events'

/**
 * Events UI Store
 * Manages UI-specific state for the events feature
 */
export const useEventsUIStore = defineStore('events-ui', () => {
  // ===== STATE =====
  const eventDateTitle = ref('')
  const calendarRef = ref<any>(null)
  const viewMode = ref<'calendar' | 'list'>('calendar')
  const sidebarCalendarDate = ref(new Date())
  const selectedEvent = ref<EventSearchResult | null>(null)
  const eventModalMode = ref<'view' | 'share' | 'confirmation' | 'delete' | 'updateRecurring' | ''>('view')
  const calendarView = ref<'month' | 'week' | 'day'>('month')
  const createEventModalMode = ref<'create'>('create')
  const showCreateEventModal = ref(false)

  const createEventsUIStore = useCreateEventsUIStore()
  // ===== ACTIONS =====
  const setEventDateTitle = (title: string) => {
    eventDateTitle.value = title
  }

  const clearEventDateTitle = () => {
    eventDateTitle.value = ''
  }

  const setCalendarRef = (ref: any) => {
    calendarRef.value = ref
  }

  const setViewMode = (mode: 'calendar' | 'list') => {
    viewMode.value = mode
  }

  const setSidebarCalendarDate = (date: Date) => {
    sidebarCalendarDate.value = new Date(date)
  }

  const setSelectedEvent = (event: EventSearchResult | null) => {
    if (event) {
      setEventModalMode('view')
    }
    selectedEvent.value = event
  }

  const setEventModalMode = (mode: 'view' | 'share' | 'confirmation' | 'delete' | 'updateRecurring' | '') => {
    eventModalMode.value = mode
  }

  const setCalendarView = (view: 'month' | 'week') => {
    calendarView.value = view
  }

  const toggleCreateEventModal = (startDate?: Date) => {
    createEventModalMode.value = 'create'
    if (showCreateEventModal.value) {
      createEventsUIStore.resetForm()
    } else if (startDate) {
      const roundedStart = roundToNext30Minutes(startDate)
      createEventsUIStore.formData.startTime = roundedStart
      createEventsUIStore.formData.endTime = roundedStart.add(DEFAULT_EVENT_DURATION_MINUTES, 'minute')
    }
    showCreateEventModal.value = !showCreateEventModal.value
  }

  // ===== RETURN =====
  return {
    // State (using computed for reactivity, not readonly)
    eventDateTitle: computed(() => eventDateTitle.value),
    calendarRef: computed(() => calendarRef.value),
    viewMode: computed(() => viewMode.value),
    sidebarCalendarDate: computed(() => sidebarCalendarDate.value),
    selectedEvent: computed(() => selectedEvent.value),
    eventModalMode: computed(() => eventModalMode.value),
    calendarView: computed(() => calendarView.value),
    createEventModalMode: computed(() => createEventModalMode.value),
    showCreateEventModal: computed(() => showCreateEventModal.value),
    // Actions
    setEventDateTitle,
    clearEventDateTitle,
    setCalendarRef,
    setViewMode,
    setSidebarCalendarDate,
    setSelectedEvent,
    setEventModalMode,
    setCalendarView,
    toggleCreateEventModal,
  }
})
